/**
 * How a player bats and bowls, on their profile (ADR-0023).
 *
 * Three facts: the batting hand, the bowling arm and the bowling type. They are shown whether or
 * not the player has played, because a squad member who has never batted can still be known to be
 * left-handed. A fact nobody has recorded reads **"Not recorded yet"**, never right-handed: a
 * guess on a profile looks like a fact.
 *
 * What this shows are the saved *defaults*, the answer the scorer's sheets are pre-filled with. What
 * was true in a given match is in that match's ledger, and changing a profile does not move it.
 *
 * Only the person who claimed the profile is offered the editor. What they save is marked as their
 * own word, so a scorer at someone else's ground cannot overwrite it from a scoring screen.
 */
import { useState } from "react";
import { BOWLING_TYPES, battingHandName, bowlingStyleName, bowlingTypeName } from "@sportsos/engine";
import type { BatterHand, BowlingArm, BowlingType, PlayerStyle } from "@sportsos/engine";
import { api } from "../api.ts";

const NOT_RECORDED = "Not recorded yet";
const armName = (arm: BowlingArm): string => (arm === "left" ? "Left arm" : "Right arm");

interface Props {
  readonly playerId: string;
  readonly style: PlayerStyle | undefined;
  /** Called with what the server saved, so the screen shows it without reloading. */
  readonly onSaved: (style: PlayerStyle) => void;
}

export function PlayStyle({ playerId, style, onSaved }: Props): React.ReactElement {
  const [editing, setEditing] = useState(false);
  const [hand, setHand] = useState<BatterHand | null>(null);
  const [arm, setArm] = useState<BowlingArm | null>(null);
  const [type, setType] = useState<BowlingType | null>(null);
  const [saving, setSaving] = useState(false);
  const [failed, setFailed] = useState(false);

  const shown = style ?? { battingHand: null, bowlingArm: null, bowlingType: null, editable: false };
  const bowlingStyle = bowlingStyleName(shown.bowlingArm, shown.bowlingType);

  const edit = (): void => {
    setHand(shown.battingHand);
    setArm(shown.bowlingArm);
    setType(shown.bowlingType);
    setFailed(false);
    setEditing(true);
  };

  const save = (): void => {
    setSaving(true);
    setFailed(false);
    // All three are sent, with null for any left unset, so what is on screen is what is saved.
    void api
      .updatePlayerStyle(playerId, { battingHand: hand, bowlingArm: arm, bowlingType: type })
      .then((saved) => {
        onSaved(saved);
        setEditing(false);
      })
      .catch(() => setFailed(true))
      .finally(() => setSaving(false));
  };

  return (
    <section aria-label="How they play">
      <div className="sechead">
        <h2 className="ds-heading">How they play</h2>
        {shown.editable && !editing ? (
          <button className="ds-btn ds-btn--secondary ds-btn--sm" onClick={edit}>
            Edit
          </button>
        ) : null}
      </div>

      {editing ? (
        <div className="ds-card stack stack--tight">
          <div>
            <div className="ds-stat-label">Batting hand</div>
            <div className="style-sides" role="group" aria-label="Batting hand">
              {(["left", "right"] as const).map((side) => (
                <button
                  key={side}
                  type="button"
                  className="style-sides__item"
                  aria-pressed={hand === side}
                  onClick={() => setHand(hand === side ? null : side)}
                >
                  {battingHandName(side)}
                </button>
              ))}
            </div>
          </div>
          <div>
            <div className="ds-stat-label">Bowling arm</div>
            <div className="style-sides" role="group" aria-label="Bowling arm">
              {(["left", "right"] as const).map((side) => (
                <button
                  key={side}
                  type="button"
                  className="style-sides__item"
                  aria-pressed={arm === side}
                  onClick={() => setArm(arm === side ? null : side)}
                >
                  {armName(side)}
                </button>
              ))}
            </div>
          </div>
          <div>
            <div className="ds-stat-label">Bowling type</div>
            <div className="style-types" role="group" aria-label="Bowling type">
              {BOWLING_TYPES.map((option) => (
                <button
                  key={option}
                  type="button"
                  className="style-types__item"
                  aria-pressed={type === option}
                  onClick={() => setType(type === option ? null : option)}
                >
                  {bowlingTypeName(option)}
                </button>
              ))}
            </div>
          </div>
          <div className="ds-caption">
            Tap a choice again to clear it. Scorers will start from what you set here, and cannot change it
            from a scoring screen. Matches already played are not changed.
          </div>
          {failed ? <div className="ds-hint ds-hint--error">Could not save. Check your connection and try again.</div> : null}
          <div className="style-step__foot">
            <button className="ds-btn ds-btn--secondary" disabled={saving} onClick={() => setEditing(false)}>
              Cancel
            </button>
            <button className="ds-btn ds-btn--primary" disabled={saving} onClick={save}>
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      ) : (
        <div className="ds-card">
          <dl className="play-style">
            <div>
              <dt>Batting hand</dt>
              <dd>{shown.battingHand === null ? NOT_RECORDED : battingHandName(shown.battingHand)}</dd>
            </div>
            <div>
              <dt>Bowling arm</dt>
              <dd>{shown.bowlingArm === null ? NOT_RECORDED : armName(shown.bowlingArm)}</dd>
            </div>
            <div>
              <dt>Bowling type</dt>
              <dd>
                {shown.bowlingType === null ? NOT_RECORDED : bowlingTypeName(shown.bowlingType)}
                {bowlingStyle !== null && shown.bowlingArm !== null && shown.bowlingType !== null ? (
                  <small>{bowlingStyle}</small>
                ) : null}
              </dd>
            </div>
          </dl>
        </div>
      )}
    </section>
  );
}
